import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../assets/logo.jpg';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'Products', href: '/products' },
  { name: 'Quote List', href: '/quote-list' },
  { name: 'Services', href: '/services' },
  { name: 'News', href: '/news' },
  { name: 'Gallery', href: '/gallery' },
  { name: 'About', href: '/about' },
  { name: 'Contact', href: '/contact' },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  return (
    <header className="site-header bg-white sticky top-0 z-50">
      <nav className="container-main" aria-label="Main navigation">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center space-x-2" aria-label={`SIAEDTS-SARL ${t('nav.home')}`}>
              <img src={logo} alt="SIAEDTS logo" className="w-14 h-14 rounded-lg" />
              <span className="site-brand-name text-xl font-bold text-gray-900 hidden sm:block">SIAEDTS-SARL</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:space-x-8">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href));
              const activeClass = isActive ? 'text-green-700' : 'text-gray-600 hover:text-green-700';
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`site-nav-link text-sm font-medium transition-colors duration-200 ${activeClass}`}
                >
                  {item.name === 'Quote List' && <svg className="mr-1 inline-block h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 7h18M5 7v12h14V7M8 7V5a4 4 0 018 0v2M9 11h6M9 15h6" /></svg>}
                  {t(`nav.${item.name === 'Quote List' ? 'quoteList' : item.name.toLowerCase()}`)}
                </Link>
              );
            })}
            {/* Language Switcher */}
            <button
              onClick={() => {
                const newLang = i18n.language.startsWith('en') ? 'fr' : 'en';
                i18n.changeLanguage(newLang);
              }}
              className="text-sm font-medium transition-colors duration-200 ml-4 hover:underline"
            >
              {i18n.language.toUpperCase()}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center">
            <button
              type="button"
              onClick={() => i18n.changeLanguage(i18n.language.startsWith('en') ? 'fr' : 'en')}
              className="mr-2 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:border-green-600 hover:text-green-700"
              aria-label={i18n.language.startsWith('en') ? 'Switch to French' : 'Switch to English'}
            >
              {i18n.language.startsWith('en') ? 'FR' : 'EN'}
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div id="mobile-menu" className="lg:hidden py-4 border-t border-gray-200 animate-slide-up">
            <div className="flex flex-col space-y-2">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href));
                const activeClass = isActive ? 'bg-green-50 text-green-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900';
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`px-3 py-2 rounded-lg text-base font-medium transition-colors ${activeClass}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name === 'Quote List' && <svg className="mr-2 inline-block h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 7h18M5 7v12h14V7M8 7V5a4 4 0 018 0v2M9 11h6M9 15h6" /></svg>}
                    {t(`nav.${item.name === 'Quote List' ? 'quoteList' : item.name.toLowerCase()}`)}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}